import { ref, computed } from 'vue';

export function useShortLinkShare() {
  const { createItem, formPending, formErrors, hasFormErrors, item } = useCrudApi('api/v2/shortLinks');

  const shortLink = ref(null);
  const isLoading = computed(() => formPending.value);
  const error = computed(() => formErrors.value);
  const hasError = computed(() => hasFormErrors.value);

  // The page's own address, for when no short link can be made: a signed-out
  // visitor cannot create one (the API answers 401), and the dialog used to
  // open saying "created" around an empty box (13 Oct 2026). A share must
  // always hand over something that works.
  const fullLinkFor = (targetPath) => {
    if (!process.client || !targetPath) return null;
    return { shortUrl: window.location.origin + targetPath, isFullLink: true };
  };

  /**
   * Create a short link for the given target path
   * @param {string} targetPath - The path to create a short link for (e.g., "/fixture/123")
   * @returns {Promise<object|null>} The short link, or the page's full address when none could be made
   */
  const createShortLink = async (targetPath) => {
    shortLink.value = null;
    try {
      await createItem({
        targetPath: targetPath
      });

      if (item.value?.shortUrl && !hasFormErrors.value) {
        shortLink.value = item.value;
        return item.value;
      }
    } catch (err) {
      console.error('Failed to create short link:', err);
    }
    shortLink.value = fullLinkFor(targetPath);
    return shortLink.value;
  };

  /**
   * Copy text to clipboard
   * @param {string} text - Text to copy
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const copyToClipboard = async (text) => {
    if (!process.client) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      return false;
    }
  };

  /**
   * Generate social share URLs
   * @param {string} url - The URL to share
   * @param {string} text - Optional text to include in the share
   * @returns {object} Object with social platform share URLs
   */
  const getSocialShareUrls = (url, text = 'Check this out!') => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);

    return {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      email: `mailto:?subject=${encodedText}&body=${encodedUrl}`
    };
  };

  /**
   * Open a URL in a new window (for social sharing)
   * @param {string} url - The URL to open
   */
  const openShareWindow = (url) => {
    if (!process.client) {
      return;
    }
    window.open(url, '_blank', 'width=600,height=400');
  };

  return {
    shortLink,
    isLoading,
    error,
    hasError,
    createShortLink,
    copyToClipboard,
    getSocialShareUrls,
    openShareWindow
  };
}
